import React, { useEffect, useState } from "react";
import { getConversationMessages } from "../../../api/auth";
import { getConversationIntelligence, getPersonProfile } from "../../../api/conversation";
import { ErrorState } from "../../shared/StateMessage";
import { Skeleton, SkeletonText } from "../../shared/Skeleton";
import { fmtCurrency, fmtNumber, fmtDuration, fmtDateLabel, titleCase, labelFor, telHref } from "../../shared/format";

// ConversationDetailPane — Phase 8. Replaces the transcript-only pane for
// voice conversations. Fires fn_get_conversation_detail (via the existing
// getConversationMessages, which re-sorts messages by timestamp — that re-sort
// MUST survive this rewrite) AND fn_get_conversation_intelligence TOGETHER on
// row select, then renders the B.6 block order:
//   1. Header        — detail.conversation (name/visitor id, date, duration, msg count, outcome)
//   2. Recording     — ALWAYS visible, never collapsed, speed control, time_to_first_caller_word_ms on the scrub bar
//   3. Inquiry       — intel.inquiry  (--type-takeaway)
//   4. What happened — intel.outcome  (--type-takeaway)
//      Insights      — intel.signals.extracted_insights (verbatim lists)
//   5. Call shape    — intel.timing / call fields (--type-label)
//   6. Signals       — intel.signals
//   7. Transcript   — collapsed; low-confidence note when provenance.transcription_confidence < 0.85
//   8. Guest card    — links out to Guest Intelligence (person_id → /visitors)
//
// caller_ever_spoke === false → blocks 3–6 collapse to one line:
//   "The caller hung up during the greeting."
//
// ON THE CHAT MODALITY THE PANE IS SIX BLOCKS, NOT EIGHT:
//   header -> inquiry -> what happened -> insights -> signals -> transcript -> guest
// The recording and call-shape blocks are NEVER MOUNTED. `detail.call` is null
// on every chat row and there is no audio to play, so those blocks do not exist
// on this modality — they are not rendered empty, not collapsed, and not handed
// a null payload for PanelShell to turn into an empty state. The decision is
// made HERE, at the composition site, from `modality`; no block decides for
// itself by looking at its data. There is no greeting to abandon either, so the
// caller_ever_spoke collapse does not apply.
//
// The transcript renders EXPANDED on chat — the opposite of voice. With no
// recording, the transcript IS the artefact.
//
// Render-only rules (plan DoD): null and zero-because-absent fields are OMITTED
// entirely — no dash, no "unknown", no $0.00. Vendor internals (costs,
// avg_latency_ms) never render. Type scale: inquiry/outcome at --type-takeaway,
// supporting detail at --type-label.

// Label/value rows across this pane share ONE column ruler.
//
// They used to be `justify-between`: label hard left, value hard right. That is
// fine in a 320px column and falls apart in the full-width pane, where a
// two-word value ends up a screen away from the label that names it and the eye
// has nothing to follow across the gap. Worse, the long prose fields — the
// barrier detail, the follow-up reason — were right-aligned paragraphs, so every
// line started at a different x and the reader lost their place on each return
// sweep.
//
// A fixed label track fixes both: because every row declares the SAME track, the
// labels line up down the section without the rows needing to share a grid
// parent, which they cannot — the sections interleave prose and lists between
// them. Values start at one predictable x, left-aligned, and are capped at a
// readable measure so a wide viewport does not stretch a sentence past the point
// where the eye can find the next line. Below `sm` the grid collapses to one
// column and the value sits under its label.
const LABEL_RULER = "sm:grid-cols-[11rem_minmax(0,1fr)]";
const VALUE_MEASURE = "max-w-[70ch]";

// Render a value ONLY when present (null/undefined → render nothing). The
// detail pane's core rule: no $0.00, no dash, no "unknown" for absent fields.
const Maybe = ({ label, value, className = "" }) => {
  // A boolean reaching this raw renders as NOTHING beside its label — React
  // drops `false` from the tree — which is exactly the bare heading this pane
  // forbids. Callers are expected to map booleans to Yes/No; this is the net
  // under that expectation, not a substitute for it.
  if (value == null || value === "" || typeof value === "boolean") return null;
  return (
    <div className={`grid grid-cols-1 ${LABEL_RULER} gap-y-0.5 sm:gap-x-6 py-2 pl-5 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${className}`}>
      <span className="gi-label !capitalize text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`gi-body text-gray-900 dark:text-gray-100 font-medium break-words ${VALUE_MEASURE}`}>{value}</span>
    </div>
  );
};

// A list of strings under one label — questions a visitor asked, gaps the site
// had, insights the extraction drew. Renders NOTHING when the list is empty, so an
// absent signal leaves no heading. `quoted` wraps each item in quotation marks for
// the visitor's own words; the extraction's own observations are not quotes.
const QuestionList = ({ title, items, quoted = true }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={`grid grid-cols-1 ${LABEL_RULER} gap-y-0.5 sm:gap-x-6 py-2 pl-5 border-b border-gray-100 dark:border-gray-800 last:border-b-0`}>
      <p className="gi-label !capitalize text-gray-500 dark:text-gray-400">{title}</p>
      <ul className={`space-y-0.5 ${VALUE_MEASURE}`}>
        {items.map((q, i) => (
          <li key={i} className="gi-body break-words text-gray-700 dark:text-gray-200">{quoted ? `"${q}"` : q}</li>
        ))}
      </ul>
    </div>
  );
};

const Badge = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
    {children}
  </span>
);

// `when` gates the whole block. Every field inside is a <Maybe>, which renders
// nothing when its value is absent — so a section whose fields are all absent
// used to leave a bare heading with empty space under it, four times over. The
// spec forbids exactly that: "Do not render an empty heading or a placeholder."
// Milliseconds -> "6.7s". One decimal, because a greeting is measured in
// hundreds of milliseconds and rounding 614ms to "1s" throws away the finding.
// Null stays null so `Maybe` omits the row; 0 is a real measurement and renders.
const secondsFromMs = (ms) => (ms == null ? null : `${(ms / 1000).toFixed(1)}s`);

// A 0-1 ratio -> "41% of the call". Formatting, not derivation: the backend owns
// the ratio and this only chooses how to write it down. Whole percent — the
// stored value carries three decimals and none of them is a finding.
const percentOfCall = (ratio) =>
  (ratio == null ? null : `${Math.round(Math.min(1, Number(ratio)) * 100)}% of the call`);

/**
 * The longest silence, and who had just spoken when it fell.
 * The role is only appended when there WAS a silence — naming who went quiet for
 * zero seconds says nothing.
 */
const silenceValue = (ms, afterRole) => {
  const shown = secondsFromMs(ms);
  if (shown == null) return null;
  if (!ms || !known(afterRole)) return shown;
  return `${shown}, after the ${afterRole} spoke`;
};

const present = (...values) => values.some((v) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));

// "unknown" is a stored classification, not a fact about the conversation, and
// the spec bans it from this pane along with dashes and nulls: "Null fields
// omitted entirely, never rendered as dashes." A field the extraction could not
// classify is an absent field here.
const known = (v) => (v == null || v === "" || v === "unknown" ? null : v);

// A list field that may arrive as null, a string, or an array.
const asList = (v) => {
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== "");
  if (v == null || v === "") return [];
  // A non-array OBJECT wrapped into a single-element list renders as
  // "[object Object]" — the same class of hole as a bare boolean. Only scalars
  // are safe to promote to a one-item list.
  if (typeof v === "object") return [];
  return [v];
};

const Section = ({ title, when = true, headerRight = null, children }) => {
  if (!when) return null;
  return (
    <section className="py-3">
      {title && (
        <div className="flex items-center justify-between gap-3 mb-3 bg-[#32B1FF]/10 dark:bg-[#32B1FF]/20 rounded-md px-3.5 py-2.5">
          <h4 className="text-sm font-medium uppercase text-[#0B6FB0] dark:text-[#7FD0FF]">{title}</h4>
          {headerRight}
        </div>
      )}
      {children}
    </section>
  );
};

// Recording — always visible, never collapsed.
//
// The player is OURS, not the browser's. `<audio controls>` draws its chrome in a
// shadow tree the page's stylesheet only partly reaches: the panel and most
// buttons take author rules, but the ⋮ overflow button (Download / Playback
// speed) matches no author-addressable pseudo-element — verified against
// ::-webkit-media-controls-overflow-button, ::-internal-media-controls-overflow-button,
// ::-webkit-media-controls-overflow-menu-button and
// ::-internal-media-controls-button-panel, none of which touch it, while
// ::-webkit-media-controls-mute-button hid its button immediately. So a stock
// grey menu sat inside a themed control with no CSS able to change it.
//
// The <audio> element stays as the playback engine and is simply not rendered.
// Everything visible here is ordinary markup that follows the theme, and the
// menu's two items are the Speed row and the Download link below.
const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

const VolumeIcon = ({ off }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4z" />
    {off ? <path d="M17 9l4 6M21 9l-4 6" /> : <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" />}
  </svg>
);

// h-11/w-11 is the spec's 44px touch floor. The transport sits inside a pill
// that was already taller than the old 32px buttons, so the target grows without
// the row growing with it.
const CONTROL_BUTTON =
  "inline-flex items-center justify-center h-11 w-11 rounded-full text-gray-600 dark:text-gray-300 hover:text-[#32B1FF] hover:bg-[#32B1FF]/10 transition-colors";

const Recording = ({ url, durationMs }) => {
  const [rate, setRate] = useState(1);
  const [audio, setAudio] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [metaDuration, setMetaDuration] = useState(0);
  if (!url) return null;

  // The row already knows the length, so the scrubber is usable before the file
  // is fetched — `preload="none"` means metadata does not arrive until playback.
  const duration = metaDuration || (durationMs ? durationMs / 1000 : 0);
  const playedPct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;

  const togglePlay = () => {
    if (!audio) return;
    if (!audio.paused) { audio.pause(); return; }
    // `play()` returns a promise in browsers and undefined where the media
    // element is not implemented, so the rejection handler has to be optional.
    const started = audio.play();
    if (started && typeof started.catch === "function") started.catch(() => {});
  };

  const seekTo = (seconds) => {
    setCurrent(seconds);
    if (audio) audio.currentTime = seconds;
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (audio) audio.muted = next;
  };

  // Generous space above (`mt-8`), a smaller one below the speed row (`pb-5`).
  // The original `my-5 py-3` doubled up with the next section's own top spacing.
  return (
    <div className="pt-3 pb-5">
      <h4 className="text-sm font-medium uppercase text-[#0B6FB0] dark:text-[#7FD0FF] mb-3 bg-[#32B1FF]/10 dark:bg-[#32B1FF]/20 rounded-md px-3.5 py-2.5">Recording</h4>
      <audio
        ref={setAudio}
        preload="none"
        src={url}
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          // A streamed recording can report Infinity until it is fully buffered;
          // taking that would blow up every percentage on this row.
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setMetaDuration(d);
        }}
        onRateChange={(e) => setRate(e.currentTarget.playbackRate)}
      />
      <div className="flex items-center gap-3 rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2">
        <button type="button" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} className={CONTROL_BUTTON}>
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
        <span className="gi-label tabular-nums text-gray-500 dark:text-gray-400">
          {fmtDuration(current)} / {fmtDuration(duration)}
        </span>
        <div className="relative flex-1">
          <input
            type="range"
            aria-label="Seek"
            className="audio-scrub w-full"
            min={0}
            max={duration || 0}
            step={0.1}
            disabled={duration <= 0}
            value={Math.min(current, duration || 0)}
            onChange={(e) => seekTo(Number(e.target.value))}
            style={{ "--pct": playedPct }}
          />
        </div>
        <button type="button" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} className={CONTROL_BUTTON}>
          <VolumeIcon off={muted} />
        </button>
      </div>
      <div className="flex items-center flex-wrap gap-2 mt-4">
        <span className="gi-label text-gray-500 dark:text-gray-400">Speed</span>
        {[0.5, 1, 1.5, 2].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => { if (audio) audio.playbackRate = r; setRate(r); }}
            className={`inline-flex items-center justify-center px-2 py-1 max-md:min-h-[44px] rounded text-xs font-medium border ${
              rate === r
                ? "bg-[#2175A8] text-white border-[#2175A8]"
                : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700"
            }`}
          >
            {r}×
          </button>
        ))}
        {/* The native ⋮ menu's one unique item. `download` only forces a save on a
            same-origin file; a signed storage URL opens the recording in a new
            tab, which is the useful fallback. */}
        <a
          href={url}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center px-2 py-1 max-md:min-h-[44px] rounded text-xs font-medium border bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:text-[#1a86c9] hover:border-[#32B1FF] dark:hover:text-[#7fd0ff] transition-colors no-underline"
        >
          Download
        </a>
      </div>
    </div>
  );
};

// Transcript — the message bubbles. Collapsed by default on voice, EXPANDED on
// chat (`defaultOpen`), where with no recording the transcript is the artefact.
// Low-confidence note
// when provenance.transcription_confidence < 0.85. Reuses the timestamp-sorted
// messages from getConversationMessages (the re-sort at api/auth.js:557–575).
const Transcript = ({ messages, confidence, defaultOpen = false, getMsgSide, getMsgText, getMsgTime, formatTime }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!messages || messages.length === 0) return null;
  const lowConf = confidence != null && confidence < 0.85;
  return (
    <Section
      title="Transcript"
      headerRight={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Hide transcript" : "Show transcript"}
          className="inline-flex items-center min-h-[44px] text-sm font-medium gi-link shrink-0 -my-2 px-2 transition-colors"
        >
          {open ? "Hide" : "Show"}
        </button>
      }
    >
      {lowConf && (
        <p className="gi-body text-amber-600 dark:text-amber-400 mb-2">
          This transcript may have transcription errors — confidence {Math.round(confidence * 100)}%.
        </p>
      )}
      {open && (
        <div className="space-y-2 mt-2">
          {messages.map((m, i) => {
            const side = getMsgSide(m);
            const text = getMsgText(m);
            const t = formatTime(getMsgTime(m));
            const isRight = side === "right";
            return (
              <div key={i} className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm border ${
                  isRight
                    ? "bg-[#328DFF] text-white border-[#1c8fd6]"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700"
                }`}>
                  <div className="whitespace-pre-wrap break-words">{text || <span className="opacity-60">(no text)</span>}</div>
                  {t && <div className={`mt-1 text-[10px] ${isRight ? "text-white/80" : "text-gray-500 dark:text-gray-400"}`}>{t}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};


// One `fn_get_person_profile` fact as display text, or null when it carries no
// value. The live shape is {key, value, value_bool, value_date, value_numeric, …}
// — there is no `label` and no `fact` field, so rendering the entry itself puts a
// raw object into a React child and throws "Objects are not valid as a React
// child", which takes the whole pane down. The string `value` is null on typed
// facts, so the typed columns are the fallback, not an afterthought.
const factText = (f) => {
  if (!f || typeof f !== "object") return null;
  let value = f.value;
  if (value == null || value === "") value = f.value_numeric;
  if (value == null) value = f.value_date;
  if (value == null && typeof f.value_bool === "boolean") value = f.value_bool ? "Yes" : "No";
  if (value == null || value === "") return null;   // omit rather than render an empty bullet
  const label = labelFor(f.key);
  return label ? `${label}: ${value}` : String(value);
};

// `relationship` comes back as a plain string ("conversation") on the deployed
// function, though an object shape appears in older payloads. Accept either; a
// string reaching `.name` silently yields undefined and the reader loses the label.
const relationshipLabel = (rel) => {
  if (rel && typeof rel === "object") {
    return rel.name || rel.display_name || labelFor(rel.relationship) || "";
  }
  return "";
};

// What the guest link should say. A person's name when one is known; otherwise
// name the destination rather than the record type — the link used to read
// "Conversation", which is `relationship`'s value and tells the reader nothing
// about where they are going.
const guestLinkLabel = (profile) =>
  relationshipLabel(profile?.relationship) || "View guest profile";

// Guest card — links out to Guest Intelligence (Visitor Intelligence) when the
// conversation has a resolved person_id. Fetches the person profile on demand.
const GuestCard = ({ personId, clientId, onOpenGuest }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!personId || !clientId) return;
    let cancelled = false;
    setLoading(true);
    getPersonProfile(clientId, personId)
      .then((d) => { if (!cancelled) setProfile(d); })
      .catch((e) => { console.error("voice guest card:", e); if (!cancelled) setProfile(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [personId, clientId]);
  if (!personId) return null;
  const facts = (profile?.facts || []).map(factText).filter(Boolean).slice(0, 3);
  return (
    <Section title="Guest">
      {/* A LEVEL, NOT A LINK-OUT, when the caller can stack one — spec §5.2
          removed the Guest Intelligence link explicitly. A reader three levels
          into one panel who is sent to another page loses the whole path back.
          The anchor remains for the call sites that have no stack: the detail
          pane still renders inside the page's own master-detail, where there is
          nowhere to push to. */}
      {onOpenGuest ? (
        <button
          type="button"
          data-testid="open-guest"
          onClick={() => onOpenGuest(personId, guestLinkLabel(profile))}
          className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium gi-link transition-colors"
        >
          {guestLinkLabel(profile)}
          <span className="text-gray-500 dark:text-gray-400">→</span>
        </button>
      ) : (
        <a href={`/visitors?person=${personId}`} className="inline-flex items-center gap-1 min-h-[44px] text-sm font-medium gi-link transition-colors no-underline">
          {guestLinkLabel(profile)}
          <span className="text-gray-500 dark:text-gray-400">→</span>
        </a>
      )}
      {loading && <p className="gi-body text-gray-500 dark:text-gray-400 mt-1">Loading profile…</p>}
      {facts.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {facts.map((text, i) => (
            <li key={i} className="gi-body text-gray-600 dark:text-gray-300">{text}</li>
          ))}
        </ul>
      )}
    </Section>
  );
};


// The pane. `convo` is the list row (has modality, person_id, etc.); the fetch
// fires on `conversationId` change. `getMsgSide/Text/Time` + `formatTime` are
// passed in so the pane reuses the page's existing message-shape helpers rather
// than duplicating them.
// The conversation's identity header — a phone-tap link (voice) or the name, then
// the date, duration, message count, outcome badge and a "Known guest" badge. It is
// block 1 of the pane AND the slide-over's title (built from the row before the
// detail fetch), so the drawer's title reads richly instead of a bare number + date.
export const ConversationHeader = ({ convo, conversation, modality = "voice", fallbackName }) => {
  const isChat = modality === "chat";
  const headerName = convo?.visitor_name || convo?.caller_name || conversation?.caller_name
    || convo?.visitor_phone || conversation?.visitor_phone
    || convo?.visitor_email || conversation?.visitor_email
    || (convo?.visitor_id ? `Visitor ${convo.visitor_id}` : null)
    || fallbackName
    || (isChat ? "Anonymous visitor" : null);
  const personId = convo?.person_id || conversation?.person_id;
  const startedAt = convo?.started_at || conversation?.started_at;
  return (
    <header className="mb-2">
      {!isChat && telHref(headerName) ? (
        <a
          href={telHref(headerName)}
          className="gi-takeaway inline-flex items-center gap-1.5 min-h-[44px] gi-link transition-colors no-underline"
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          {headerName}
        </a>
      ) : (
        <h3 className="gi-takeaway gi-link">{headerName || "Unnamed caller"}</h3>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {startedAt && <span className="gi-label gi-link">{fmtDateLabel(startedAt)}</span>}
        {!isChat && convo?.duration_seconds != null && <span className="gi-label text-gray-500 dark:text-gray-400">{fmtDuration(convo.duration_seconds)}</span>}
        {convo?.message_count != null && <span className="gi-label text-gray-500 dark:text-gray-400">{fmtNumber(convo.message_count)} messages</span>}
        {convo?.outcome && <Badge>{labelFor(convo.outcome)}</Badge>}
        {personId && <Badge>Known guest</Badge>}
      </div>
    </header>
  );
};

const ConversationDetailPane = ({
  conversationId,
  clientId,
  convo,
  // "voice" or "chat". Decides which blocks exist at all — see the block list at
  // the top of this file. Defaults to voice so the existing call sites are
  // unchanged by this prop's arrival.
  modality = "voice",
  // The label the LIST already shows for this row (`Guest ab12cd` when nobody
  // identified themselves). Passed in rather than recomputed so the pane and the
  // row never disagree about who this is.
  fallbackName,
  getMsgSide,
  getMsgText,
  getMsgTime,
  formatTime,
  // Present only when the pane is mounted as a slide-over level.
  onOpenGuest,
  // Suppress block 1 (the identity header) when the slide-over already shows it as
  // its title — avoids rendering the same header twice in the drawer.
  hideHeader = false,
}) => {
  const [detail, setDetail] = useState(null);     // { data: messages, conversation, call }
  const [intel, setIntel] = useState(null);       // { inquiry, outcome, signals, timing, provenance }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!conversationId) { setDetail(null); setIntel(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Fire BOTH fetches together. getConversationMessages reuses the existing
    // timestamp re-sort (api/auth.js:557–575) — that fix MUST survive here.
    Promise.all([
      getConversationMessages(conversationId).catch((e) => { console.error("voice detail:", e); return null; }),
      getConversationIntelligence(conversationId).catch((e) => { console.error("voice intel:", e); return null; }),
    ]).then(([d, i]) => {
      if (cancelled) return;
      setDetail(d);
      setIntel(i);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [conversationId]);

  if (loading && !detail) {
    return (
      <div className="p-4 space-y-3" role="status" aria-label="Loading conversation">
        <Skeleton className="h-8 w-2/3" />
        <SkeletonText lines={4} />
      </div>
    );
  }
  if (error) return <ErrorState variant="inset" message={error} />;

  const isChat = modality === "chat";
  const conversation = detail?.conversation || convo;
  const call = detail?.call || null;
  const messages = Array.isArray(detail?.data) ? detail.data : [];
  // A chat cannot be abandoned during a greeting, so the collapse never applies
  // there — it is a voice concept, gated on the modality rather than on a field
  // that happens to be null for both reasons.
  const callerEverSpoke = isChat
    || (intel?.caller_ever_spoke !== false && call?.caller_ever_spoke !== false);
  const prov = intel?.provenance || {};
  const inquiry = intel?.inquiry || {};
  const outcome = intel?.outcome || {};
  const signals = intel?.signals || {};
  const insights = (signals.extracted_insights && typeof signals.extracted_insights === "object")
    ? signals.extracted_insights : {};
  const timing = intel?.timing || {};


  // ANONYMITY IS NORMAL ON CHAT, not missing data: the header (ConversationHeader)
  // handles the name/date fallbacks. `personId` gates the guest block below.
  const personId = convo?.person_id || conversation?.person_id;

  return (
    <div className="p-4" data-testid={isChat ? "chat-detail-pane" : "voice-detail-pane"}>
      {/* 1. Header — suppressed when the slide-over already shows it as its title. */}
      {!hideHeader && (
        <ConversationHeader convo={convo} conversation={conversation} modality={modality} fallbackName={fallbackName} />
      )}

      {/* 2. Recording — voice only, and always visible there, never collapsed.
          NEVER MOUNTED on chat: `detail.call` is null on every chat row and
          there is no audio. Gated on the modality, not on the url being absent,
          so a chat row can never fall into a "recording is loading" shape. */}
      {!isChat && (
        <Recording
          url={call?.recording_url || conversation?.recording_url}
          durationMs={convo?.duration_seconds ? convo.duration_seconds * 1000 : null}
        />
      )}

      {/* caller_ever_spoke === false → the middle blocks collapse to one line.
          Voice only; `callerEverSpoke` is forced true on chat. */}
      {!callerEverSpoke ? (
        <Section>
          <p className="gi-takeaway text-gray-700 dark:text-gray-300">The caller hung up during the greeting.</p>
        </Section>
      ) : (
        <>
          {/* 3. Inquiry — what they came for.
              Gated on CONTENT, not on the object existing: `intel.inquiry` comes
              back populated-but-all-null, so testing the object left a heading
              over empty space. The field names are the DEPLOYED ones, verified
              live: stay_type_sought / booking_urgency / primary_intent /
              decision_stage / group_size / date_flexibility / price_sensitivity.
              The older summary/text/question/stay_type/urgency names are kept as
              leading alternatives because a payload carrying them still renders. */}
          <Section
            title="Inquiry"
            when={present(inquiry.summary, inquiry.text, inquiry.question,
              known(inquiry.primary_intent), known(inquiry.stay_type_sought), known(inquiry.stay_type),
              known(inquiry.booking_urgency), known(inquiry.urgency), known(inquiry.decision_stage),
              inquiry.group_size, known(inquiry.date_flexibility), known(inquiry.price_sensitivity),
              inquiry.budget_range_mentioned, inquiry.check_in_date, inquiry.num_nights,
              asList(inquiry.secondary_intents).length ? asList(inquiry.secondary_intents) : null,
              asList(inquiry.hookup_requirements).length ? asList(inquiry.hookup_requirements) : null)}
          >
            {present(inquiry.summary, inquiry.text, inquiry.question) && (
              <p className="gi-takeaway text-gray-700 dark:text-gray-300">{inquiry.summary || inquiry.text || inquiry.question}</p>
            )}
            <Maybe label="Wanted" value={known(inquiry.primary_intent) && labelFor(inquiry.primary_intent)} />
            <Maybe label="Stay type" value={known(inquiry.stay_type_sought || inquiry.stay_type) && labelFor(inquiry.stay_type_sought || inquiry.stay_type)} />
            <Maybe label="Urgency" value={known(inquiry.booking_urgency || inquiry.urgency) && labelFor(inquiry.booking_urgency || inquiry.urgency)} />
            <Maybe label="Decision stage" value={known(inquiry.decision_stage) && labelFor(inquiry.decision_stage)} />
            <Maybe label="Group size" value={inquiry.group_size != null ? fmtNumber(inquiry.group_size) : null} />
            <Maybe label="Date flexibility" value={known(inquiry.date_flexibility) && labelFor(inquiry.date_flexibility)} />
            <Maybe label="Price sensitivity" value={known(inquiry.price_sensitivity) && labelFor(inquiry.price_sensitivity)} />
            {/* Mapped like every other boolean in this pane. The extraction
                emits real booleans in this object (group_booking, return_visitor),
                and a bare `false` here would print the label over nothing. */}
            <Maybe
              label="Budget mentioned"
              value={inquiry.budget_range_mentioned === true ? "Yes"
                : inquiry.budget_range_mentioned === false ? "No"
                : known(inquiry.budget_range_mentioned)}
            />
            <Maybe label="Hook-ups" value={asList(inquiry.hookup_requirements).map(labelFor).join(", ") || null} />
            <Maybe label="Also asked about" value={asList(inquiry.secondary_intents).map(labelFor).join(", ") || null} />
            <Maybe label="Check-in" value={inquiry.check_in_date && fmtDateLabel(inquiry.check_in_date)} />
            <Maybe label="Nights" value={inquiry.num_nights != null ? fmtNumber(inquiry.num_nights) : null} />
          </Section>

          {/* 4. What happened (outcome) */}
          <Section
            title="What happened"
            when={present(outcome.summary, outcome.resolution, outcome.result, outcome.barrier_detail,
              known(outcome.outcome), known(outcome.resolution_status), outcome.reservation_value,
              outcome.estimated_lost_value, outcome.booked, outcome.followup_reason,
              asList(outcome.conversion_barriers).length ? asList(outcome.conversion_barriers) : null)}
          >
            {present(outcome.summary, outcome.resolution, outcome.result) && (
              <p className="gi-takeaway text-gray-700 dark:text-gray-300">{outcome.summary || outcome.resolution || outcome.result}</p>
            )}
            <Maybe label="Outcome" value={known(outcome.outcome) && labelFor(outcome.outcome)} />
            <Maybe label="Resolution" value={known(outcome.resolution_status) && labelFor(outcome.resolution_status)} />
            <Maybe label="Reservation value" value={outcome.reservation_value != null ? fmtCurrency(outcome.reservation_value) : null} />
            {/* An estimated loss of zero is "we saw no loss", which the reader
                cannot act on — and $0.00 reads as a measurement. Omitted. */}
            <Maybe label="Estimated lost value" value={outcome.estimated_lost_value ? fmtCurrency(outcome.estimated_lost_value) : null} />
            <Maybe label="Booked" value={outcome.booked === true ? "Yes" : outcome.booked === false ? "No" : null} />
            <Maybe label="What got in the way" value={asList(outcome.conversion_barriers).map(labelFor).join(", ") || null} />
            <Maybe label="Detail" value={outcome.barrier_detail} />
            <Maybe label="Follow-up needed because" value={outcome.followup_reason} />
          </Section>

          {/* Insights — signals.extracted_insights, the per-conversation observations
              the extraction writes (operational insights, marketing opportunities,
              unexpected details, notable quotes). Rendered verbatim under the same
              blue band as every other block. Only the canonical key names are read:
              a handful of records carry misspelt keys from the extraction (e.g.
              "operrational_insights"), and repairing those is the backend's job,
              not a fuzzy match here. Each field arrives as either a list or a single
              string, so every one goes through asList. */}
          <Section
            title="Insights"
            when={present(
              asList(insights.operational_insights).length ? asList(insights.operational_insights) : null,
              asList(insights.marketing_opportunities).length ? asList(insights.marketing_opportunities) : null,
              asList(insights.unexpected_details).length ? asList(insights.unexpected_details) : null,
              asList(insights.notable_quotes).length ? asList(insights.notable_quotes) : null,
            )}
          >
            <QuestionList title="Operational insights" items={asList(insights.operational_insights)} quoted={false} />
            <QuestionList title="Marketing opportunities" items={asList(insights.marketing_opportunities)} quoted={false} />
            <QuestionList title="Unexpected details" items={asList(insights.unexpected_details)} quoted={false} />
            <QuestionList title="Notable quotes" items={asList(insights.notable_quotes)} />
          </Section>

          {/* 5. Call shape — VOICE ONLY, and NEVER MOUNTED on chat. Every field
              in it is a property of two people speaking over a phone line; none
              of it exists for typed messages, and a live probe on 2026-08-29
              confirmed all eleven `timing` fields come back null on chat.

              THE FIELD NAMES WERE WRONG UNTIL STAGE D. This block read
              `duration_seconds`, `talk_time_seconds`, `silence_seconds` and
              `interruptions` on `intel.timing`. Live, only `turn_count` of the
              five existed — so the section rendered exactly one row on every
              voice conversation. Worse, `agent_talk_ratio` was in the `when`
              gate that decides whether the section mounts and had no row of its
              own: the block was being held on screen by the one number it never
              showed. Same shape as the `calls`/`count` bucket bug.

              The deployed names, probed over 60 voice conversations:
                greeting_duration_ms         60/60   614..41208
                agent_talk_ratio             60/60   0.123..1
                longest_silence_ms           60/60   0..39941  (non-zero on 39)
                longest_silence_after_role   39/60   agent | caller
                interruptions_by_agent       60/60   0 on every one
                turn_count                   60/60   1..83 */}
          {!isChat && (
            <Section
              title="Call shape"
              when={present(timing.greeting_duration_ms, timing.agent_talk_ratio,
                timing.longest_silence_ms, timing.turn_count,
                timing.interruptions_by_agent ? timing.interruptions_by_agent : null)}
            >
              {/* §5.1's order for this block: greeting length, talk ratio,
                  longest silence, interruptions. */}
              <Maybe label="Greeting length" value={secondsFromMs(timing.greeting_duration_ms)} />
              {/* The agent's share of the talking, which is what the field
                  measures — "Talk ratio" alone does not say whose. */}
              <Maybe label="Agent talk time" value={percentOfCall(timing.agent_talk_ratio)} />
              {/* ZERO IS A MEASUREMENT HERE, NULL IS AN ABSENCE, and they must
                  not render the same way: a call with no measurable gap really
                  did have none, while a missing field means nobody measured. So
                  0 renders as 0.0s and null renders as nothing at all. Who fell
                  silent is only meaningful when there was a silence. */}
              <Maybe
                label="Longest silence"
                value={silenceValue(timing.longest_silence_ms, timing.longest_silence_after_role)}
              />
              {/* "Interruptions when non-zero" — §5.1, verbatim. This is the one
                  field in the pane where a real 0 is deliberately omitted: an
                  agent that never talked over the caller is not news, and a row
                  reading 0 on every conversation is noise the reader learns to
                  skip. Probed live: 0 on all 60, so this row does not render on
                  this property today and its render path is unit-tested rather
                  than browser-verified. */}
              <Maybe
                label="Interruptions"
                value={timing.interruptions_by_agent ? fmtNumber(timing.interruptions_by_agent) : null}
              />
              <Maybe label="Turns" value={timing.turn_count != null ? fmtNumber(timing.turn_count) : null} />
            </Section>
          )}

          {/* 6. Signals — the deployed keys are unanswered_questions,
              website_gaps, local_knowledge_questions, competitor_mentions,
              self_reported_source and caller_type. On chat the first two are the
              page's whole thesis in one conversation: what this visitor asked
              that the site did not answer. */}
          <Section
            title="Signals"
            when={present(known(signals.sentiment), signals.contains_complaint,
              signals.transferred_to_human, known(signals.caller_type), known(signals.self_reported_source),
              asList(signals.topics).length ? asList(signals.topics) : null,
              asList(signals.unanswered_questions).length ? asList(signals.unanswered_questions) : null,
              asList(signals.website_gaps).length ? asList(signals.website_gaps) : null,
              asList(signals.local_knowledge_questions).length ? asList(signals.local_knowledge_questions) : null,
              asList(signals.competitor_mentions).length ? asList(signals.competitor_mentions) : null)}
          >
            <Maybe label="Sentiment" value={known(signals.sentiment) && labelFor(signals.sentiment)} />
            <Maybe label={isChat ? "Visitor type" : "Caller type"} value={known(signals.caller_type) && labelFor(signals.caller_type)} />
            <Maybe label="Said they found us via" value={known(signals.self_reported_source) && labelFor(signals.self_reported_source)} />
            <Maybe label="Complaint" value={signals.contains_complaint === true ? "Yes" : signals.contains_complaint === false ? "No" : null} />
            <Maybe label="Transferred" value={signals.transferred_to_human === true ? "Yes" : signals.transferred_to_human === false ? "No" : null} />
            <Maybe label="Competitors mentioned" value={asList(signals.competitor_mentions).map(labelFor).join(", ") || null} />
            {/* Verbatim, in the visitor's own words — the reason this page
                exists. Not summarised, not truncated. */}
            <QuestionList title="Asked but not answered" items={asList(signals.unanswered_questions)} />
            <QuestionList title="Answers the website was missing" items={asList(signals.website_gaps)} />
            <QuestionList title="Asked about the area" items={asList(signals.local_knowledge_questions)} />
            {asList(signals.topics).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {asList(signals.topics).map((t, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">{labelFor(t)}</span>
                ))}
              </div>
            )}
          </Section>
        </>
      )}

      {/* 7. Transcript — collapsed by default (both voice and chat); opened on demand. */}
      <Transcript
        messages={messages}
        confidence={prov.transcription_confidence}
        defaultOpen={false}
        getMsgSide={getMsgSide}
        getMsgText={getMsgText}
        getMsgTime={getMsgTime}
        formatTime={formatTime}
      />

      {/* 8. Guest card → Guest Intelligence */}
      <GuestCard onOpenGuest={onOpenGuest} personId={personId} clientId={clientId} />
    </div>
  );
};

export default ConversationDetailPane;

